/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { IslandType } from '../../public.enums';
import { Panel } from '../panel.class';
import { Vertex3 } from '../../../spatial-classes';

const SKU = 'UTILITY-CUTOUT';

/**
 * Utility Panel cutout configuration
 */
export type UtilityPanelCutoutConfig = {
	cutoutCount: number;
	cutoutWidth: number;
	cutoutHeight: number;
	sideMargin: number;
	centerGap: number;
	topBottomMargin: number;
};

/**
 * Side panel cutout configuration based on island type
 */
type SidePanelCutoutConfig = {
	cutoutWidth: number;
	cutoutHeight: number;
};

type CreateCutoutParams = {
	panel: Panel;
	cutoutWidth: number;
	cutoutHeight: number;
	centerX: number;
};

const SINGLE_CUTOUT_WIDTH = 32;
const CUTOUT_HEIGHT = 20;
const TOP_BOTTOM_MARGIN = 7.25;
const MIN_PANEL_WIDTH = 48;
const TWO_CUTOUT_THRESHOLD = 66;
const CENTER_GAP_DOUBLE_THRESHOLD = 90;

/**
 * Gets the side margin for two-cutout back panels based on panel width tier.
 * Within each tier, cutout widths scale linearly with panel width.
 * @param panelWidth Width of the panel in inches
 * @returns Side margin in inches
 */
const getSideMargin = (panelWidth: number): number => {
	if (panelWidth < 126) return 8;
	if (panelWidth < 138) return 12;
	if (panelWidth < 162) return 14;
	return 16;
};

/**
 * Gets the utility panel cutout configuration based on panel width.
 * Cutout widths scale proportionally with panel width — extra inches
 * beyond standard foot sizes are split evenly between cutouts.
 *
 * Single cutout (< 66"): fixed 32" cutout width, margins absorb the rest.
 * Two cutouts (>= 66"): sideMargin from tier, centerGap derived from sideMargin,
 * cutoutWidth = (panelWidth - 2 * sideMargin - centerGap) / 2.
 *
 * @param panelWidth Width of the panel in inches
 * @returns Configuration object or null if panel is too narrow
 */
export const getUtilityPanelConfig = (panelWidth: number): UtilityPanelCutoutConfig | null => {
	if (panelWidth < MIN_PANEL_WIDTH) return null;

	if (panelWidth < TWO_CUTOUT_THRESHOLD) {
		return {
			cutoutCount: 1,
			cutoutWidth: SINGLE_CUTOUT_WIDTH,
			cutoutHeight: CUTOUT_HEIGHT,
			sideMargin: (panelWidth - SINGLE_CUTOUT_WIDTH) / 2,
			centerGap: 0,
			topBottomMargin: TOP_BOTTOM_MARGIN
		};
	}

	const sideMargin = getSideMargin(panelWidth);
	const centerGap = sideMargin * (panelWidth < CENTER_GAP_DOUBLE_THRESHOLD ? 1 : 2);
	const cutoutWidth = (panelWidth - 2 * sideMargin - centerGap) / 2;

	return {
		cutoutCount: 2,
		cutoutWidth,
		cutoutHeight: CUTOUT_HEIGHT,
		sideMargin,
		centerGap,
		topBottomMargin: TOP_BOTTOM_MARGIN
	};
};

/**
 * Gets the side panel cutout configuration based on island type
 * @param islandType Type of island
 * @returns Configuration object with cutout dimensions
 */
export const getSidePanelCutoutConfig = (islandType: IslandType): SidePanelCutoutConfig => {
	return { cutoutWidth: 15, cutoutHeight: islandType === IslandType.Bar ? 25 : 20 };
};

/**
 * Creates a single utility cutout item on a panel at the specified x position
 * @param params Cutout creation parameters
 */
const createCutout = (params: CreateCutoutParams): void => {
	const { panel, cutoutWidth, cutoutHeight, centerX } = params;
	const { kitchen } = panel.refMap;
	const component = kitchen.context.components.findSku(SKU);
	const cutoutSize = { width: cutoutWidth, height: cutoutHeight, depth: 3 };

	const panelItemGroup = panel.panelItemGroups.new();
	panelItemGroup.center = new Vertex3({ dataIn: { x: centerX }});
	panelItemGroup.items.new({
		item: {
			override: true,
			component: {
				sku: SKU,
				categoryId: component.category?.id,
				placement: component.placement,
				size: cutoutSize,
				cutout: cutoutSize,
				msrp: 0
			}
		}
	});
};

/**
 * Adds utility panel cutouts to a side panel (single centered cutout)
 * @param panel Panel to add cutout to
 */
const addSidePanelCutout = (panel: Panel): void => {
	const { island } = panel.refMap;
	if (!island) {
		console.warn('No island found for side panel');
		return;
	}

	const { cutoutWidth, cutoutHeight } = getSidePanelCutoutConfig(island.type);
	createCutout({ panel, cutoutWidth, cutoutHeight, centerX: 0 });
};

/**
 * Adds utility panel cutouts to a back panel based on its width
 * @param panel Panel to add cutouts to
 */
const addBackPanelCutouts = (panel: Panel): void => {
	const panelWidth = panel.size.width;
	const config = getUtilityPanelConfig(panelWidth);

	if (!config) {
		console.warn(`No utility panel configuration found for panel width: ${panelWidth}"`);
		return;
	}

	const { cutoutWidth, cutoutHeight, sideMargin, cutoutCount } = config;

	if (cutoutCount === 1) {
		createCutout({ panel, cutoutWidth, cutoutHeight, centerX: 0 });
		return;
	}

	const halfPanelWidth = panelWidth / 2;
	const leftCenterX = -halfPanelWidth + sideMargin + (cutoutWidth / 2);
	const rightCenterX = halfPanelWidth - sideMargin - (cutoutWidth / 2);

	createCutout({ panel, cutoutWidth, cutoutHeight, centerX: leftCenterX });
	createCutout({ panel, cutoutWidth, cutoutHeight, centerX: rightCenterX });
};

/**
 * Removes existing utility cutout items from a panel's item groups.
 * Matches both legacy 'CUTOUT' SKU and current 'UTILITY-CUTOUT' for backward compatibility.
 * @param panel Panel to clear cutouts from
 */
const clearUtilityCutouts = (panel: Panel): void => {
	const { children } = panel.panelItemGroups;
	for (let i = children.length - 1; i >= 0; i--) {
		const hasUtilityCutout = children[i].items.children.some((panelItem) => {
			return panelItem.item.category?.isUtilityCutout || panelItem.item.sku === SKU;
		});
		if (hasUtilityCutout) {
			children[i].cleanup();
			children.splice(i, 1);
		}
	}
};

/**
 * Adds utility panel cutouts to a panel based on its type (back panel or side panel).
 * Clears any existing cutouts first, making this safe to call on panel resize.
 * @param panel Panel to add utility cutouts to
 */
export const addUtilityPanelCutouts = (panel: Panel): void => {
	clearUtilityCutouts(panel);
	if (panel.isSidePanel) {
		addSidePanelCutout(panel);
	} else if (panel.isBackPanel) {
		addBackPanelCutouts(panel);
	}
};
